import { Tooltip } from "@src/common/components";
import { generateTooltipContent } from "../utils/shipmentRowFormatter";
import {
  ArchiveBoxIcon,
  ScaleIcon,
  ServerStackIcon,
} from "@heroicons/react/24/outline";
import { faPallet } from "@fortawesome/free-solid-svg-icons";
import { useCourierLocale } from "@src/common/courierLocale";

// Render the Font Awesome pallet glyph as an inline SVG from its icon data.
// Avoids pulling in @fortawesome/react-fontawesome + fontawesome-svg-core
// (neither is installed) for a single icon, and matches the inline-SVG style
// of the heroicons rendered beside it.
function PalletIcon({ className }: { className?: string }) {
  const [width, height, , , svgPathData] = faPallet.icon;
  const path = Array.isArray(svgPathData) ? svgPathData[0] : svgPathData;
  return (
    <svg
      className={className}
      viewBox={`0 0 ${width} ${height}`}
      fill="currentColor"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  );
}

type PackageProps = {
  numPackages?: number;
  packageSize?: string;
  packageWeightInPounds?: number;
  estimatedSkidSpaces?: number | null;
};

type PackageTooltipProps = {
  pkg?: PackageProps;
};

export const PackageTooltip: React.FC<PackageTooltipProps> = ({ pkg }) => {
  const { formatWeight } = useCourierLocale();

  const estimatedSkidSpaces = pkg?.estimatedSkidSpaces;
  const shouldShowSkidSpaces =
    estimatedSkidSpaces != null &&
    estimatedSkidSpaces > 0;

  const hasPackageInfo =
    pkg &&
    (pkg.numPackages ||
      (pkg.packageSize && pkg.packageSize.trim() !== "") ||
      pkg.packageWeightInPounds);

  if (hasPackageInfo || shouldShowSkidSpaces) {
    return (
      <Tooltip
        content={generateTooltipContent(pkg)}
        placement="bottom"
      >
        <p className="font-normal truncate">
          {pkg?.numPackages && (
            <span className="mr-1">
              <ArchiveBoxIcon className="inline h-4 w-4" /> {pkg.numPackages}
            </span>
          )}
          {pkg?.packageSize && (
            <span className="mr-1">
              <ServerStackIcon className="inline h-4 w-4" /> {pkg.packageSize}
            </span>
          )}
          {pkg?.packageWeightInPounds && (
            <span className="mr-1">
              <ScaleIcon className="inline h-4 w-4" />{" "}
              {formatWeight(pkg.packageWeightInPounds)}
            </span>
          )}
          {shouldShowSkidSpaces && (
            <span className="mr-1">
              <PalletIcon className="inline h-4 w-4" /> {estimatedSkidSpaces}
            </span>
          )}
        </p>
      </Tooltip>
    );
  }
  return <p className="font-normal truncate text-slate-400">NA</p>;
};
